/* eslint-disable react/jsx-max-props-per-line */
import React from 'react';
import PropTypes from 'prop-types';
import { Button, Chip, Divider, Stack, Typography } from '@mui/material';
import InputV2 from '../InputV2';

const chipShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  selected: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  size: PropTypes.string,
});

const renderChip = (chip) => (
  <Chip
    key={chip.id}
    label={chip.label}
    clickable
    size={chip.size || 'small'}
    variant={chip.selected ? 'filled' : 'outlined'}
    color={chip.selected ? 'primary' : 'default'}
    onClick={chip.onClick}
  />
);

const ChipGroupSection = ({ label, chips, compact }) => (
  <Stack
    spacing={0.5}
    sx={compact ? { flex: '0 0 auto', width: 'auto' } : { flex: 1, minWidth: 0 }}
  >
    <Typography variant="caption" color="text.secondary" fontWeight={600}>
      {label}
    </Typography>
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {chips.map(renderChip)}
    </Stack>
  </Stack>
);

ChipGroupSection.propTypes = {
  label: PropTypes.string.isRequired,
  chips: PropTypes.arrayOf(chipShape).isRequired,
  compact: PropTypes.bool,
};

ChipGroupSection.defaultProps = {
  compact: false,
};

const FilterChipBarV2 = ({
  searchLabel,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  searchSx,
  chips,
  secondaryChips,
  chipGroups,
  chipGroupsLayout,
  filterActive,
  filteredCount,
  totalCount,
  countLabel,
  onClear,
  actions,
  showCount,
}) => (
  <Stack spacing={1.5} sx={{ mb: 2 }}>
    {(searchLabel || onSearchChange) && (
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-end' }}>
        <InputV2
          label={searchLabel || 'Buscar'}
          name="filterChipBarSearch"
          value={searchValue || ''}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          sx={{ flex: 1, ...searchSx }}
        />
        {actions?.map((action) => (
          <Button
            key={action.label}
            variant={action.variant || 'outlined'}
            startIcon={action.startIcon}
            onClick={action.onClick}
          >
            {action.label}
          </Button>
        ))}
      </Stack>
    )}

    {chipGroups?.length > 0 ? (
      <Stack spacing={1.25}>
        {chipGroupsLayout === 'row' ? (
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'stretch', md: 'stretch' }}
            spacing={0}
            useFlexGap
            sx={{
              flexWrap: { md: 'wrap' },
              justifyContent: 'flex-start',
              columnGap: { md: 0 },
              rowGap: { xs: 1.25, md: 0 },
            }}
          >
            {chipGroups.map((group, index) => (
              <React.Fragment key={group.label}>
                {index > 0 && (
                  <>
                    <Divider
                      flexItem
                      orientation="vertical"
                      sx={{
                        mx: 2.5,
                        borderColor: 'divider',
                        display: { xs: 'none', md: 'block' },
                      }}
                    />
                    <Divider sx={{ display: { xs: 'block', md: 'none' } }} />
                  </>
                )}
                <ChipGroupSection compact label={group.label} chips={group.chips} />
              </React.Fragment>
            ))}
          </Stack>
        ) : (
          chipGroups.map((group) => (
            <ChipGroupSection key={group.label} label={group.label} chips={group.chips} />
          ))
        )}
      </Stack>
    ) : (
      <>
        {chips?.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {chips.map(renderChip)}
            {filterActive && onClear && (
              <Chip label="Limpar" variant="outlined" onClick={onClear} onDelete={onClear} />
            )}
          </Stack>
        )}

        {secondaryChips?.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {secondaryChips.map(renderChip)}
            {filterActive && !chips?.length && onClear && (
              <Chip label="Filtro ativo" color="warning" variant="outlined" onDelete={onClear} />
            )}
          </Stack>
        )}
      </>
    )}

    {showCount && filterActive && filteredCount != null && totalCount != null && (
      <Typography variant="caption" color="text.secondary">
        {filteredCount} de {totalCount} {countLabel}
      </Typography>
    )}
  </Stack>
);

FilterChipBarV2.propTypes = {
  searchLabel: PropTypes.string,
  searchValue: PropTypes.string,
  onSearchChange: PropTypes.func,
  searchPlaceholder: PropTypes.string,
  searchSx: PropTypes.object,
  chips: PropTypes.arrayOf(chipShape),
  secondaryChips: PropTypes.arrayOf(chipShape),
  chipGroups: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    chips: PropTypes.arrayOf(chipShape).isRequired,
  })),
  chipGroupsLayout: PropTypes.oneOf(['column', 'row']),
  filterActive: PropTypes.bool,
  filteredCount: PropTypes.number,
  totalCount: PropTypes.number,
  countLabel: PropTypes.string,
  onClear: PropTypes.func,
  actions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    variant: PropTypes.string,
    startIcon: PropTypes.node,
  })),
  showCount: PropTypes.bool,
};

FilterChipBarV2.defaultProps = {
  searchLabel: 'Buscar',
  searchValue: '',
  onSearchChange: null,
  searchPlaceholder: '',
  searchSx: null,
  chips: [],
  secondaryChips: [],
  chipGroups: [],
  chipGroupsLayout: 'column',
  filterActive: false,
  filteredCount: null,
  totalCount: null,
  countLabel: 'itens',
  onClear: null,
  actions: [],
  showCount: true,
};

export default FilterChipBarV2;
